/**
 * The sweep that applies {@link migratedBridgeIds} to the active project.
 *
 * The RULE lives in `bridgeAffinity.ts`, which imports nothing, so it can be
 * driven by a pure-logic smoke. This file is the half that reaches the store.
 *
 * # Why this file is small
 *
 * Because `resolveBridge` was made node-aware rather than 58 call sites
 * being rewritten. Every outbound path - `fanoutTargets`,
 * `bridgeConnectedReactive`, `anyBridgeConnectedReactive`, `httpClientFor`,
 * `fireAction` - already funnels through it, so once it accepts a node id
 * the whole app does. What is left is rewriting what is STORED, which is
 * this.
 *
 * # It is safe to run on a synced document
 *
 * Every machine computes the SAME node id for the same physical bridge, so
 * two machines migrating concurrently converge rather than fight. That is
 * precisely the property the device-local row id lacked, and it is why the
 * pedal version of this bug produced a last-writer-wins coin flip between
 * machines.
 */

import { createEffect, on } from 'solid-js';
import { bridges } from '../opbridge/store.ts';
import { editItem } from './store.ts';
import { activeProject } from './projectStore.ts';
import type { LayoutItem } from './projectStore.ts';
import { migratedBridgeIds, type AffinityRow } from './bridgeAffinity.ts';

/** Migrate every item on every page of the active project. Idempotent. */
export function migrateBridgeAffinity(): void {
  const rows = bridges();
  if (rows.length === 0) return;
  // Accept EITHER key on the way in, so a half-migrated list (some row ids,
  // some node ids - which is the normal state mid-rollout) resolves fully.
  const rowFor = (id: string): AffinityRow | null =>
    rows.find((b) => b.id === id || b.nodeId === id) ?? null;

  const p = activeProject();
  if (!p) return;
  let migrated = 0;
  for (const page of p.pages) {
    for (const item of page.items as LayoutItem[]) {
      const ids = (item as { bridgeIds?: string[] }).bridgeIds;
      const next = migratedBridgeIds(ids, rowFor);
      if (!next) continue;
      editItem(item.id, { bridgeIds: next } as Parameters<typeof editItem>[1]);
      migrated += 1;
    }
  }
  if (migrated > 0) {
    console.info(
      `[affinity] migrated ${migrated} item(s) from device-local bridge row ids to ` +
      'stable node ids - a row id does not survive the mesh',
    );
  }
}

/** Wire the sweep. Re-runs when the bridge list or its node ids change,
 *  because `nodeId` is backfilled asynchronously by the first `/health` and
 *  an item can become migratable with nothing else having happened. */
export function installBridgeAffinityMigration(): void {
  createEffect(on(
    () => bridges().map((b) => `${b.id}:${b.nodeId ?? ''}`).join(','),
    () => { migrateBridgeAffinity(); },
  ));
}
